import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import { toast } from 'sonner';

const NO_TEAM_VALUE = '__none__';

/**
 * Admin-only edit dialog for tournament registrations. Lets admins fix the
 * two fields that drift most often: `age_category` and `team_id`. Backend
 * keeps team rosters in sync (removes from old team, adds to new). Payment
 * fields are deliberately untouched.
 */
const EditRegistrationDialog = ({
  open, onOpenChange, registration, tournament, teams = [], onSaved,
}) => {
  const ageCategories = tournament?.age_categories || [];
  const teamsForCategory = (cat) =>
    (teams || []).filter(
      (t) => !cat || (t.age_category || '').toLowerCase() === cat.toLowerCase(),
    );

  const [ageCategory, setAgeCategory] = useState('');
  const [teamId, setTeamId] = useState(NO_TEAM_VALUE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && registration) {
      setAgeCategory(registration.age_category || '');
      setTeamId(registration.team_id || NO_TEAM_VALUE);
    }
  }, [open, registration]);

  // If admin changes age category, clear a now-mismatched team selection.
  useEffect(() => {
    if (teamId === NO_TEAM_VALUE) return;
    const team = teams.find((t) => t.id === teamId);
    if (team && (team.age_category || '').toLowerCase() !== (ageCategory || '').toLowerCase()) {
      setTeamId(NO_TEAM_VALUE);
    }
  }, [ageCategory, teamId, teams]);

  if (!registration) return null;

  const isCancelled = registration.status === 'cancelled';
  const dirty =
    ageCategory !== (registration.age_category || '') ||
    (teamId === NO_TEAM_VALUE ? !!registration.team_id : teamId !== registration.team_id);

  const handleSave = async () => {
    if (!dirty || isCancelled) return;
    setSaving(true);
    try {
      const payload = {};
      if (ageCategory !== (registration.age_category || '')) {
        payload.age_category = ageCategory;
      }
      const newTeamId = teamId === NO_TEAM_VALUE ? '' : teamId;
      const oldTeamId = registration.team_id || '';
      if (newTeamId !== oldTeamId) payload.team_id = newTeamId;

      await api.patch(
        `/tournaments/${tournament.id}/registrations/${registration.id}/admin-edit`,
        payload,
      );
      toast.success('Registration updated');
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update registration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-registration-dialog">
        <DialogHeader>
          <DialogTitle>Edit Registration</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-slate-50 p-3 rounded text-sm">
            <p><strong>Player:</strong> {registration.player_name || '—'}</p>
            <p><strong>Parent:</strong> {registration.parent_name || '—'}</p>
            <p><strong>Status:</strong> {registration.status}{registration.payment_status ? ` · ${registration.payment_status}` : ''}</p>
          </div>

          {isCancelled && (
            <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
              Reinstate this registration before editing.
            </p>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-age-category">Age Category</Label>
            <Select
              value={ageCategory}
              onValueChange={setAgeCategory}
              disabled={isCancelled || saving}
            >
              <SelectTrigger id="edit-age-category" data-testid="edit-reg-age-category">
                <SelectValue placeholder="Select age category" />
              </SelectTrigger>
              <SelectContent>
                {ageCategories.map((c) => (
                  <SelectItem key={c} value={c} data-testid={`edit-reg-age-option-${c}`}>
                    {c.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-team">Team</Label>
            <Select
              value={teamId}
              onValueChange={setTeamId}
              disabled={isCancelled || saving}
            >
              <SelectTrigger id="edit-team" data-testid="edit-reg-team">
                <SelectValue placeholder="Select team" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_TEAM_VALUE} data-testid="edit-reg-team-option-none">— No team —</SelectItem>
                {teamsForCategory(ageCategory).map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`edit-reg-team-option-${t.id}`}>
                    {t.name} ({(t.age_category || '').toUpperCase()})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">
              Only teams matching the selected age category are listed. Changing teams updates the team roster automatically.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!dirty || isCancelled || saving}
            data-testid="edit-reg-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditRegistrationDialog;
